import type { ShowLockedPeriods } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { useCallback, useState } from "react";
import { invalidateInvoiceQueries } from "@api/invoice.api";
import { resolveInvoiceSourceDate } from "@common/helpers/domains/accounting/invoiceSourceDate";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { Invoice } from "@common/types/invoice";
import {
    ScheduleSpecificDateApplyRequest,
    ScheduleSpecificDateApplyResult,
} from "@components/accounting/schedules/manual/scheduleSpecificDateApply";
import { useScheduleSpecificDateApply } from "@components/accounting/schedules/manual/useScheduleSpecificDateApply";
import { buildAccruedBilling } from "@components/accounting/schedules/scheduleApplyAccruedHelpers";
import { SchedulePickerTemplateWithAnchors } from "@components/accounting/schedules/scheduleApplyConfirmHelpers";
import { ScheduleDrawer } from "@components/accounting/schedules/ScheduleDrawer";
import { useApplyInvoiceSchedule } from "./useApplyInvoiceSchedule";

export interface ScheduleConfirmRequest {
    invoice: Invoice;
    template: SchedulePickerTemplateWithAnchors;
    // Set for a line-scoped apply; omitted for whole-invoice.
    sourceLineIds?: string[];
    amount: number;
    sourceLabel: string;
}

type InvoiceSpecificDateRequest = ScheduleSpecificDateApplyRequest & {
    invoice: Invoice;
    sourceLineIds?: string[];
};

function toSpecificDateRequest(
    options: ScheduleConfirmRequest,
): InvoiceSpecificDateRequest {
    const { invoice, template, sourceLineIds } = options;
    const sourceDate = resolveInvoiceSourceDate(invoice);
    return {
        templateId: template.id,
        templateName: template.name,
        entityId: invoice.entityId,
        sourceType: ScheduleSourceType.invoice,
        amount: options.amount,
        sourceLabel: options.sourceLabel,
        sourceKey: `${invoice.id}:${sourceLineIds?.join(",") ?? "invoice"}`,
        sourceDate,
        billing: buildAccruedBilling({
            sourceDate,
            amount: options.amount,
            noun: "Invoice",
        }),
        invoice,
        sourceLineIds,
    };
}

/**
 * Owns the Specific-date step for the invoice Schedule columns: the schedule
 * modal that collects the date. The cells cannot host it themselves, so the
 * page renders it and the cells raise a request through table meta; the
 * request resolves to whether a schedule was applied once the modal closes.
 */
export function useInvoiceScheduleApply({
    onApplied,
    showLockedPeriods,
}: {
    onApplied?: () => Promise<void>;
    showLockedPeriods?: ShowLockedPeriods;
} = {}) {
    const afterApply = onApplied ?? invalidateInvoiceQueries;
    const { applySchedule, isApplying, linkedConflictModal } =
        useApplyInvoiceSchedule({ showLockedPeriods });
    const applyPending = useCallback(
        (
            pending: InvoiceSpecificDateRequest,
            result: ScheduleSpecificDateApplyResult,
        ) =>
            applySchedule({
                templateId: pending.templateId,
                target: {
                    entityId: pending.entityId,
                    invoiceId: pending.invoice.id,
                    amount: pending.amount,
                    sourceLineIds: pending.sourceLineIds,
                    ...result,
                },
                onApplied: afterApply,
            }),
        [afterApply, applySchedule],
    );
    const { open: openSpecificDate, modal: specificDateModal } =
        useScheduleSpecificDateApply<InvoiceSpecificDateRequest>({
            apply: applyPending,
            isApplying,
        });
    const [creatingForEntityId, setCreatingForEntityId] = useState<
        number | null
    >(null);

    const onRequestScheduleConfirm = useCallback(
        (options: ScheduleConfirmRequest) =>
            openSpecificDate(toSpecificDateRequest(options)),
        [openSpecificDate],
    );

    const onScheduleApplied = useCallback(async () => {
        await afterApply();
    }, [afterApply]);

    const onCreateSchedule = useCallback(
        (options: { entityId: number }) =>
            setCreatingForEntityId(options.entityId),
        [],
    );

    const scheduleOverlays = (
        <>
            {specificDateModal}
            {linkedConflictModal}
            <ScheduleDrawer
                open={creatingForEntityId != null}
                templateId={null}
                onClose={() => setCreatingForEntityId(null)}
            />
        </>
    );

    return {
        onRequestScheduleConfirm,
        onScheduleApplied,
        onCreateSchedule,
        scheduleOverlays,
    };
}
